import React, { useState } from 'react'
import {
  TextField,
  Button,
  Typography,
  Box,
  Divider,
  SelectChangeEvent,
  Alert
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PersonIcon from '@mui/icons-material/Person'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import ClearIcon from '@mui/icons-material/Clear'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import {
  formatCardNumber,
  formatCvv,
  formatExpirationDate,
  formatZipCode,
  removeSpaces,
  validateCardNumber,
  validateCVV,
  validateExpirationDate,
  validateZipCode
} from 'utils/formatUtils'
import { NewPaymentInfo, PaymentFormData, PaymentInfo } from 'hooks/usePayment'
import AddressFields from 'components/AddressFields'
import NameFields from 'components/NameFields'
import { useTranslation } from 'react-i18next'

type PaymentFormProps = {
  initialValues: PaymentFormData
  onSubmit: (paymentDetails: NewPaymentInfo | PaymentInfo) => Promise<void>
  onCancel: () => void
  mode: 'add' | 'edit'
}

const PaymentForm: React.FC<PaymentFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  mode
}) => {
  const { t } = useTranslation()
  const [formValues, setFormValues] = useState<PaymentFormData>({
    paymentProfileId: initialValues.paymentProfileId || '',
    cardNumber: initialValues.cardNumber || '',
    expirationDate: initialValues.expirationDate || '',
    cvv: initialValues.cvv || '',
    billingAddress: {
      firstName: initialValues.billingAddress.firstName || '',
      lastName: initialValues.billingAddress.lastName || '',
      addressLine1: initialValues.billingAddress.addressLine1 || '',
      addressLine2: initialValues.billingAddress.addressLine2 || '',
      city: initialValues.billingAddress.city || '',
      state: initialValues.billingAddress.state || '',
      zipCode: initialValues.billingAddress.zipCode || ''
    }
  })

  const [errorMessages, setErrorMessages] = useState<Record<string, string>>({
    cardNumber: '',
    expirationDate: '',
    cvv: '',
    firstName: '',
    lastName: '',
    addressLine1: '',
    city: '',
    state: '',
    zipCode: ''
  })

  const [formError, setFormError] = useState<boolean>(false)

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let formattedValue = value

    if (name === 'cardNumber') {
      formattedValue = formatCardNumber(value)
      setFormValues({
        ...formValues,
        cardNumber: formattedValue
      })
    } else if (name === 'expirationDate') {
      formattedValue = formatExpirationDate(value)
      setFormValues({
        ...formValues,
        expirationDate: formattedValue
      })
    } else if (name === 'cvv') {
      formattedValue = formatCvv(value)
      setFormValues({
        ...formValues,
        cvv: formattedValue
      })
    } else if (name === 'zipCode') {
      formattedValue = formatZipCode(value)
      setFormValues({
        ...formValues,
        billingAddress: {
          ...formValues.billingAddress,
          zipCode: formattedValue
        }
      })
    } else if (name in formValues.billingAddress) {
      setFormValues({
        ...formValues,
        billingAddress: { ...formValues.billingAddress, [name]: value }
      })
    } else {
      setFormValues({ ...formValues, [name]: value })
    }
  }

  const handleBlur = (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target
    let errorMessage = ''

    if (name === 'cardNumber') {
      errorMessage = validateCardNumber(value)
    } else if (name === 'expirationDate') {
      errorMessage = validateExpirationDate(value)
        ? ''
        : t('myAccount.paymentMethods.errors.expirationDateInvalid')
    } else if (name === 'cvv') {
      errorMessage = validateCVV(value)
        ? ''
        : t('myAccount.paymentMethods.errors.cvvInvalid')
    } else if (name === 'zipCode') {
      errorMessage = validateZipCode(value)
        ? ''
        : t('myAccount.paymentMethods.errors.zipCodeInvalid')
    } else if (name in formValues.billingAddress) {
      errorMessage =
        value.trim() === ''
          ? t('myAccount.paymentMethods.errors.fieldRequired')
          : ''
    }

    setErrorMessages(prev => ({
      ...prev,
      [name]: errorMessage
    }))
  }

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target

    if (name && name in formValues.billingAddress) {
      setFormValues({
        ...formValues,
        billingAddress: {
          ...formValues.billingAddress,
          [name]: value
        }
      })

      setErrorMessages(prev => ({
        ...prev,
        [name]:
          value.trim() === ''
            ? t('myAccount.paymentMethods.errors.fieldRequired')
            : ''
      }))
    }
  }

  const handleClear = () => {
    setFormValues({
      paymentProfileId: '',
      cardNumber: '',
      expirationDate: '',
      cvv: '',
      billingAddress: {
        firstName: '',
        lastName: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zipCode: ''
      }
    })

    setErrorMessages({
      cardNumber: '',
      expirationDate: '',
      cvv: '',
      firstName: '',
      lastName: '',
      addressLine1: '',
      city: '',
      state: '',
      zipCode: ''
    })

    setFormError(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const newErrors: Record<string, string> = {
      ...(mode === 'add' && {
        cardNumber: validateCardNumber(formValues.cardNumber),
        cvv: validateCVV(formValues.cvv)
          ? ''
          : t('myAccount.paymentMethods.errors.cvvInvalid')
      }),
      expirationDate: validateExpirationDate(formValues.expirationDate)
        ? ''
        : t('myAccount.paymentMethods.errors.expirationDateInvalid'),
      firstName:
        formValues.billingAddress.firstName.trim() === ''
          ? t('myAccount.paymentMethods.errors.firstNameRequired')
          : '',
      lastName:
        formValues.billingAddress.lastName.trim() === ''
          ? t('myAccount.paymentMethods.errors.lastNameRequired')
          : '',
      addressLine1:
        formValues.billingAddress.addressLine1.trim() === ''
          ? t('myAccount.paymentMethods.errors.addressLine1Required')
          : '',
      city:
        formValues.billingAddress.city.trim() === ''
          ? t('myAccount.paymentMethods.errors.cityRequired')
          : '',
      state:
        formValues.billingAddress.state.trim() === ''
          ? t('myAccount.paymentMethods.errors.stateRequired')
          : '',
      zipCode: validateZipCode(formValues.billingAddress.zipCode)
        ? ''
        : t('myAccount.paymentMethods.errors.zipCodeInvalid')
    }

    setErrorMessages(newErrors)

    const hasErrors = Object.values(newErrors).some(value => value !== '')
    setFormError(hasErrors)

    if (!hasErrors) {
      const fullAddress = `${formValues.billingAddress.addressLine1}${
        formValues.billingAddress.addressLine2
          ? `, ${formValues.billingAddress.addressLine2}`
          : ''
      }`.trim()

      let submissionData: NewPaymentInfo | PaymentInfo

      if (mode === 'edit' && formValues.paymentProfileId) {
        submissionData = {
          paymentProfileId: formValues.paymentProfileId,
          expirationDate: formValues.expirationDate,
          billingAddress: {
            firstName: formValues.billingAddress.firstName,
            lastName: formValues.billingAddress.lastName,
            address: fullAddress,
            city: formValues.billingAddress.city,
            state: formValues.billingAddress.state,
            zipCode: formValues.billingAddress.zipCode
          }
        }
      } else {
        const cardNumber = removeSpaces(formValues.cardNumber)
        submissionData = {
          cardNumber,
          expirationDate: formValues.expirationDate,
          cvv: formValues.cvv,
          billingAddress: {
            firstName: formValues.billingAddress.firstName,
            lastName: formValues.billingAddress.lastName,
            address: fullAddress,
            city: formValues.billingAddress.city,
            state: formValues.billingAddress.state,
            zipCode: formValues.billingAddress.zipCode
          }
        }
      }

      await onSubmit(submissionData)
    }
  }

  return (
    <Box sx={{ p: 4 }}>
      {formError && (
        <Alert
          severity='error'
          icon={<ErrorOutlineIcon />}
          sx={{
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-message': {
              fontWeight: 500
            }
          }}
        >
          {t('myAccount.paymentMethods.errors.allFieldsRequired')}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontWeight: 700,
            color: 'text.primary'
          }}
        >
          {mode === 'edit'
            ? t('myAccount.paymentMethods.editPaymentDetails')
            : t('myAccount.paymentMethods.addPaymentMethod')}
        </Typography>

        {mode === 'add' && (
          <Button
            variant='outlined'
            size='small'
            startIcon={<ClearIcon />}
            onClick={handleClear}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t('myAccount.paymentMethods.clear')}
          </Button>
        )}
      </Box>

      <Divider sx={{ mb: 3 }} />

      <Box
        component='form'
        noValidate
        autoComplete='off'
        onSubmit={handleSubmit}
      >
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <PersonIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.paymentMethods.billingInformation')}
            </Typography>
          </Box>

          <NameFields
            formValues={{
              firstName: formValues.billingAddress.firstName,
              lastName: formValues.billingAddress.lastName
            }}
            errors={{
              firstName: errorMessages.firstName,
              lastName: errorMessages.lastName
            }}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <LocationOnIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.paymentMethods.billingAddress')}
            </Typography>
          </Box>

          <AddressFields
            formValues={{
              addressLine1: formValues.billingAddress.addressLine1,
              addressLine2: formValues.billingAddress.addressLine2,
              city: formValues.billingAddress.city,
              state: formValues.billingAddress.state,
              zipCode: formValues.billingAddress.zipCode
            }}
            errors={{
              addressLine1: errorMessages.addressLine1,
              city: errorMessages.city,
              state: errorMessages.state,
              zipCode: errorMessages.zipCode
            }}
            handleInputChange={handleInputChange}
            handleBlur={handleBlur}
            handleSelectChange={handleSelectChange}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CreditCardIcon sx={{ color: 'primary.main', fontSize: 22 }} />
            <Typography variant='h6' sx={{ fontWeight: 700 }}>
              {t('myAccount.paymentMethods.cardDetails')}
            </Typography>
          </Box>

          {mode === 'add' && (
            <TextField
              label={t('myAccount.paymentMethods.cardNumber')}
              name='cardNumber'
              value={formValues.cardNumber}
              onChange={handleInputChange}
              onBlur={handleBlur}
              fullWidth
              margin='dense'
              variant='outlined'
              required
              error={Boolean(errorMessages.cardNumber)}
              helperText={errorMessages.cardNumber}
              inputProps={{ maxLength: 19 }}
              size='small'
            />
          )}

          <Box display='flex' gap={2}>
            <TextField
              label={t('myAccount.paymentMethods.expirationDate')}
              name='expirationDate'
              value={formValues.expirationDate}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder='MM/YY'
              fullWidth
              margin='dense'
              variant='outlined'
              required
              error={Boolean(errorMessages.expirationDate)}
              helperText={
                errorMessages.expirationDate
                  ? t('myAccount.paymentMethods.errors.expirationDateInvalid')
                  : ''
              }
              size='small'
            />

            {mode === 'add' && (
              <TextField
                label={t('myAccount.paymentMethods.cvv')}
                name='cvv'
                value={formValues.cvv}
                onChange={handleInputChange}
                onBlur={handleBlur}
                fullWidth
                margin='dense'
                variant='outlined'
                required
                error={Boolean(errorMessages.cvv)}
                helperText={errorMessages.cvv}
                inputProps={{ maxLength: 3 }}
                size='small'
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button
            variant='outlined'
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            fullWidth
            sx={{
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.paymentMethods.goBack')}
          </Button>
          <Button
            variant='contained'
            color='primary'
            type='submit'
            startIcon={<SaveIcon />}
            fullWidth
            sx={{
              py: 1,
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 400
            }}
          >
            {t('myAccount.paymentMethods.savePayment')}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default PaymentForm
